import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Login from './pages/Login';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Users = lazy(() => import('./pages/Users'));
const Logs = lazy(() => import('./pages/Logs'));
const SubIdFinder = lazy(() => import('./pages/tools/SubIdFinder'));
const DeclineAnalyzer = lazy(() => import('./pages/tools/DeclineAnalyzer'));
const Billing = lazy(() => import('./pages/Billing'));
const KeitaroConversions = lazy(() => import('./pages/tools/KeitaroConversions'));
const QualityReport = lazy(() => import('./pages/tools/QualityReport'));
const KeitaroManager = lazy(() => import('./pages/tools/KeitaroManager'));
const BuyerTeams = lazy(() => import('./pages/BuyerTeams'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const TrafficShield = lazy(() => import('./pages/TrafficShield'));
const KpiDashboard = lazy(() => import('./pages/KpiDashboard'));
const SecuritySettings = lazy(() => import('./pages/SecuritySettings'));

const ProtectedRoute = ({ children, adminOnly = false, section }) => {
  const { isAuthenticated, loading, isAdmin, canAccess } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0a0a0f' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #2a2a3a', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (adminOnly && !isAdmin) return <Navigate to="/" />;
  if (section && !canAccess(section)) return <Navigate to="/" />;

  return <Layout>{children}</Layout>;
};

const App = () => {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#fff', backgroundColor: '#0a0a0f' }}><div style={{ width: '40px', height: '40px', border: '3px solid #2a2a3a', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /></div>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute section="analytics"><AnalyticsDashboard /></ProtectedRoute>} />
          <Route path="/tools/subid-finder" element={<ProtectedRoute adminOnly><SubIdFinder /></ProtectedRoute>} />
          <Route path="/tools/decline-analyzer" element={<ProtectedRoute adminOnly><DeclineAnalyzer /></ProtectedRoute>} />
          <Route path="/tools/keitaro-conversions" element={<ProtectedRoute section="statistics"><KeitaroConversions /></ProtectedRoute>} />
          <Route path="/tools/quality-report" element={<ProtectedRoute section="quality"><QualityReport /></ProtectedRoute>} />
          <Route path="/tools/keitaro-manager" element={<ProtectedRoute section="keitaro-manager"><KeitaroManager /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute section="billing"><Billing /></ProtectedRoute>} />
          <Route path="/buyer-teams" element={<ProtectedRoute adminOnly><BuyerTeams /></ProtectedRoute>} />
          <Route path="/traffic-shield" element={<ProtectedRoute section="traffic-shield"><TrafficShield /></ProtectedRoute>} />
          <Route path="/kpi" element={<ProtectedRoute section="kpi"><KpiDashboard /></ProtectedRoute>} />
          <Route path="/security" element={<ProtectedRoute><SecuritySettings /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute adminOnly><Users /></ProtectedRoute>} />
          <Route path="/logs" element={<ProtectedRoute adminOnly><Logs /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
